import React, { useState, useRef, useId, useCallback } from "react";

interface BoltyWrapperProps {
  children: React.ReactNode;
  className?: string;
  placement?: "top" | "bottom";
  position?: "center" | "left" | "right";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
}

export function BoltyCharacter({
  size = "md",
  placement = "top",
  position = "center",
  isPopped = false,
  isWaving = false,
}: {
  size?: "sm" | "md" | "lg";
  placement?: "top" | "bottom";
  position?: "center" | "left" | "right";
  isPopped?: boolean;
  isWaving?: boolean;
}) {
  const rawId = useId();
  const fillId = `boltFill-${rawId.replace(/:/g, "")}`;
  const shineId = `boltShine-${rawId.replace(/:/g, "")}`;

  const sizeClasses = {
    sm: "w-[48px] h-[60px]",
    md: "w-[62px] h-[78px]",
    lg: "w-[74px] h-[92px]",
  }[size];

  const posClass = {
    center: "pos-center",
    left: "pos-left",
    right: "pos-right",
  }[position];

  return (
    <div
      aria-hidden="true"
      className={`bolty-character placement-${placement} ${posClass} ${sizeClasses} ${
        isPopped ? "is-popped" : ""
      } ${isWaving ? "is-waving" : ""}`}
    >
      <svg viewBox="0 0 100 120" className="w-full h-full block">
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff9a3d" />
            <stop offset="55%" stopColor="#ef3b2d" />
            <stop offset="100%" stopColor="#b6291f" />
          </linearGradient>
          <linearGradient id={shineId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Left arm */}
        <path
          className="bolty-arm-left"
          d="M32 62 C18 66 10 74 12 84"
          stroke="#b6291f"
          strokeWidth="7"
          strokeLinecap="round"
          fill="none"
        />

        {/* Right arm (waves) */}
        <path
          className="bolty-arm-right"
          d="M64 58 C80 56 90 48 92 38"
          stroke="#b6291f"
          strokeWidth="7"
          strokeLinecap="round"
          fill="none"
        />

        {/* Body: signature chunky lightning bolt */}
        <path
          d="M58 4 L22 56 L44 56 L34 96 L82 46 L56 46 Z"
          fill={`url(#${fillId})`}
          stroke="#8f1d16"
          strokeWidth="3"
          strokeLinejoin="round"
        />
        <path
          d="M58 4 L22 56 L44 56 L34 96 L82 46 L56 46 Z"
          fill={`url(#${shineId})`}
          opacity="0.5"
        />

        {/* Blinking eyes */}
        <g className="bolty-blink">
          <ellipse cx="44" cy="42" rx="5.5" ry="6.5" fill="#1a1a1a" />
          <ellipse cx="62" cy="36" rx="5.5" ry="6.5" fill="#1a1a1a" />
        </g>
        {/* Eye catchlights */}
        <circle cx="46" cy="40" r="1.6" fill="#fff" />
        <circle cx="64" cy="34" r="1.6" fill="#fff" />

        {/* Friendly smile */}
        <path
          d="M48 52 Q56 60 66 50"
          stroke="#1a1a1a"
          strokeWidth="3.4"
          fill="none"
          strokeLinecap="round"
        />

        {/* Blushing cheeks */}
        <ellipse cx="36" cy="48" rx="4.5" ry="2.6" fill="#ff8a2b" opacity="0.6" />
        <ellipse cx="70" cy="44" rx="4.5" ry="2.6" fill="#ff8a2b" opacity="0.5" />

        {/* Little feet */}
        <ellipse cx="38" cy="98" rx="7" ry="3.4" fill="#8f1d16" />
      </svg>
    </div>
  );
}

export function BoltyWrapper({
  children,
  className = "",
  placement = "top",
  position = "center",
  size = "md",
  disabled = false,
}: BoltyWrapperProps) {
  const [isPopped, setIsPopped] = useState(false);
  const [isWaving, setIsWaving] = useState(false);
  const waveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchTimerRef = useRef<NodeJS.Timeout | null>(null);

  const triggerPop = useCallback(() => {
    if (disabled) return;
    setIsPopped(true);

    if (waveTimerRef.current) clearTimeout(waveTimerRef.current);
    waveTimerRef.current = setTimeout(() => {
      setIsWaving(true);
    }, 450);
  }, [disabled]);

  const triggerReset = useCallback(() => {
    setIsPopped(false);
    setIsWaving(false);
    if (waveTimerRef.current) clearTimeout(waveTimerRef.current);
    if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
  }, []);

  const handleTouchStart = useCallback(() => {
    triggerPop();
    if (touchTimerRef.current) clearTimeout(touchTimerRef.current);
    touchTimerRef.current = setTimeout(() => {
      triggerReset();
    }, 2800);
  }, [triggerPop, triggerReset]);

  return (
    <div
      className={`bolty-wrapper ${className}`}
      onMouseEnter={triggerPop}
      onMouseLeave={triggerReset}
      onTouchStart={handleTouchStart}
    >
      <BoltyCharacter
        size={size}
        placement={placement}
        position={position}
        isPopped={isPopped}
        isWaving={isWaving}
      />
      <div className="relative z-10 w-full sm:w-auto flex items-center justify-center">
        {children}
      </div>
    </div>
  );
}
